import React, { useEffect, useState } from 'react';
import { X, Search, ChevronRight, Book } from 'lucide-react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { useHelp } from '../../contexts/HelpContext';
import { useHelpSearch } from '../../hooks/useHelpSearch';
import { HelpArticle } from '../../types/help';
import { getRelatedArticles } from '../../data/help/articles';

interface HelpPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialSearchQuery?: string;
  currentRoute?: string;
}

export const HelpPanel: React.FC<HelpPanelProps> = ({
  isOpen,
  onClose,
  initialSearchQuery = '',
  currentRoute,
}) => {
  const {
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    searchResults,
    categories,
    clearSearch,
  } = useHelpSearch();

  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);

  useEffect(() => {
    if (initialSearchQuery) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery, setSearchQuery]);

  // Get context-aware suggestions based on current route
  const contextualArticles = React.useMemo(() => {
    if (!currentRoute) return [];
    return searchResults
      .filter(result => result.article.route === currentRoute)
      .slice(0, 3);
  }, [currentRoute, searchResults]);

  const handleArticleClick = (article: HelpArticle) => {
    setSelectedArticle(article);
  };

  const handleBack = () => {
    setSelectedArticle(null);
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(selectedCategory === category ? null : category);
    setSelectedArticle(null);
  };

  const relatedArticles = selectedArticle
    ? getRelatedArticles(selectedArticle.id)
    : [];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-40"
      />

      {/* Panel */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white dark:bg-secondary-800 shadow-2xl z-50 flex flex-col"
      >
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-secondary-700 p-4 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-center gap-2">
            <Book className="h-6 w-6" />
            <h2 className="text-xl font-semibold">Help Center</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-secondary-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search help articles..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="mt-3 flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryClick(category)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-200 hover:bg-gray-100 dark:hover:bg-secondary-600 border border-gray-300 dark:border-secondary-600'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {selectedArticle ? (
            <ArticleView
              article={selectedArticle}
              relatedArticles={relatedArticles}
              onBack={handleBack}
              onArticleClick={handleArticleClick}
            />
          ) : (
            <ArticleList
              searchResults={searchResults}
              contextualArticles={contextualArticles}
              searchQuery={searchQuery}
              onArticleClick={handleArticleClick}
            />
          )}
        </div>
      </motion.div>
    </>
  );
};

// Article List Component
interface ArticleListProps {
  searchResults: Array<{ article: HelpArticle; score?: number }>;
  contextualArticles: Array<{ article: HelpArticle; score?: number }>;
  searchQuery: string;
  onArticleClick: (article: HelpArticle) => void;
}

const ArticleList: React.FC<ArticleListProps> = ({
  searchResults,
  contextualArticles,
  searchQuery,
  onArticleClick,
}) => {
  return (
    <div className="p-4">
      {contextualArticles.length > 0 && !searchQuery && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-3">
            Relevant to this page
          </h3>
          <div className="space-y-2">
            {contextualArticles.map(({ article }) => (
              <ArticleCard
                key={article.id}
                article={article}
                onClick={() => onArticleClick(article)}
              />
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-3">
          {searchQuery ? `${searchResults.length} results` : 'All Articles'}
        </h3>
        {searchResults.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-secondary-400">
            <Book className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-secondary-600" />
            <p>No articles found</p>
            <p className="text-sm mt-1">Try different keywords or browse by category</p>
          </div>
        ) : (
          <div className="space-y-2">
            {searchResults.map(({ article }) => (
              <ArticleCard
                key={article.id}
                article={article}
                onClick={() => onArticleClick(article)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Article Card Component
interface ArticleCardProps {
  article: HelpArticle;
  onClick: () => void;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, onClick }) => {
  // Strip markdown and get plain text for preview
  const getPreviewText = (content: string) => {
    return content
      .replace(/#{1,6}\s/g, '') // Remove heading markers
      .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.+?)\*/g, '$1') // Remove italic
      .replace(/`(.+?)`/g, '$1') // Remove inline code
      .replace(/\[(.+?)\]\(.+?\)/g, '$1') // Remove links, keep text
      .replace(/^\s*[-*+]\s/gm, '') // Remove list markers
      .replace(/^\s*\d+\.\s/gm, '') // Remove numbered list markers
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .trim()
      .substring(0, 150);
  };

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 mb-1">
            {article.title}
          </h4>
          <p className="text-sm text-gray-600 dark:text-secondary-400 mb-2 line-clamp-2">
            {getPreviewText(article.content)}...
          </p>
          <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 rounded">
            {article.category}
          </span>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400 dark:text-secondary-500 group-hover:text-blue-600 ml-2 flex-shrink-0" />
      </div>
    </button>
  );
};

// Article View Component
interface ArticleViewProps {
  article: HelpArticle;
  relatedArticles: HelpArticle[];
  onBack: () => void;
  onArticleClick: (article: HelpArticle) => void;
}

const ArticleView: React.FC<ArticleViewProps> = ({
  article,
  relatedArticles,
  onBack,
  onArticleClick,
}) => {
  const { navigateAndClose } = useHelp();

  return (
    <div>
      {/* Back button */}
      <div className="sticky top-0 bg-white dark:bg-secondary-800 border-b border-gray-200 dark:border-secondary-700 p-4 z-10">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 font-medium"
        >
          <ChevronRight className="h-5 w-5 transform rotate-180" />
          Back to articles
        </button>
      </div>

      {/* Article content */}
      <div className="p-6">
        <div className="mb-4">
          <span className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 rounded-full mb-3">
            {article.category}
          </span>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{article.title}</h1>
        </div>

        {article.videoUrl && (
          <div className="mb-6 rounded-lg overflow-hidden bg-gray-100 dark:bg-secondary-700 aspect-video">
            <iframe
              src={article.videoUrl}
              className="w-full h-full"
              title={article.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        <div className="prose prose-blue max-w-none text-gray-700 dark:text-secondary-200 leading-relaxed">
          <ReactMarkdown
            components={{
              h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-6 mb-4">{children}</h1>,
              h2: ({ children }) => <h2 className="text-xl font-bold text-gray-900 dark:text-white mt-5 mb-3">{children}</h2>,
              h3: ({ children }) => <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-4 mb-2">{children}</h3>,
              p: ({ children }) => <p className="mb-4 text-gray-700 dark:text-secondary-200">{children}</p>,
              ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-1">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-1">{children}</ol>,
              li: ({ children }) => <li className="text-gray-700 dark:text-secondary-200">{children}</li>,
              code: ({ node, className, children, ...props }) => {
                const inline = !className;
                return inline ? (
                  <code className="bg-gray-100 dark:bg-secondary-700 text-red-600 dark:text-red-400 px-1 py-0.5 rounded text-sm font-mono" {...props}>
                    {children}
                  </code>
                ) : (
                  <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono my-4" {...props}>
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => <pre className="my-4">{children}</pre>,
              strong: ({ children }) => <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>,
              a: ({ children, href }) => {
                // Check if it's an internal link (starts with /)
                const isInternal = href?.startsWith('/');
                
                if (isInternal && href) {
                  return (
                    <button
                      onClick={() => navigateAndClose(href)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 underline font-medium inline-flex items-center gap-1"
                    >
                      {children}
                      <span className="text-xs">→</span>
                    </button>
                  );
                }
                
                return (
                  <a href={href} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 underline" target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                );
              },
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-600 dark:text-secondary-400 my-4">
                  {children}
                </blockquote>
              ),
            }}
          >
            {article.content}
          </ReactMarkdown>
        </div>

        {/* Related articles */}
        {relatedArticles.length > 0 && (
          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-secondary-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Related Articles
            </h3>
            <div className="space-y-2">
              {relatedArticles.map((relatedArticle) => (
                <button
                  key={relatedArticle.id}
                  onClick={() => onArticleClick(relatedArticle)}
                  className="w-full text-left p-3 bg-gray-50 dark:bg-secondary-900 border border-gray-200 dark:border-secondary-700 rounded-lg hover:border-blue-500 hover:bg-white transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600">
                        {relatedArticle.title}
                      </h4>
                      <span className="text-xs text-gray-500 dark:text-secondary-400">
                        {relatedArticle.category}
                      </span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400 dark:text-secondary-500 group-hover:text-blue-600" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
